import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth-compat";
import {
  Pencil, CheckCircle, BookOpen,
  Users, ShieldAlert, Trash2,
  Clock, MapPin, Layers, GraduationCap, Target,
  DollarSign, Star, Copy, Tag,
  CalendarDays, BarChart2, ChevronRight,
  FlaskConical, RefreshCw, Info,
} from "lucide-react";
import { BackLink } from "@/components/layout/BackLink";
import { getOfferingTypeTheme } from "@/lib/offeringTypeTheme";
import { Button } from "@/components/ui/button";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import {
  getOfferingById, publishOfferingById,
  unpublishOfferingById, deleteTeacherOfferings,
} from "@/services/OfferingServices";
import { toast } from "@/hooks/use-toast";
import { currencySymbol } from "@/lib/currency";
import { useMyOfferings } from "@/hooks/useMyOfferings";
import { findLeftoverPlaceholders } from '@/lib/offeringPlaceholders';
import { checkOfferingDuplicates } from "@/lib/offeringDuplicateCheck";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTeachingModes, useGradeBands, useCurricula, useAcceptedLevels } from "@/hooks/useValueCatalog";
import { PublishRestrictionNotice } from "@/components/teacher/offering/PublishRestrictionNotice";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { useTeacherEntitlements } from "@/hooks/use-teacher-entitlements";
import {
  computeProactivePublishBlock, isOfferingTypeStillAllowed, parsePublishBlockReason,
  type OfferingPublishBlockReason,
} from "@/lib/publishRestrictions";

/* ─── type config ───
 * Colour + icon come from the shared offering-type theme (`@/lib/offeringTypeTheme`)
 * so this page always matches search cards, the wizard, sidebar, and dashboard
 * rails. `COURSE` is a legacy alias for `CRASH_COURSE` (see `cloneOffering`
 * below, which already treats them as the same type) so it reuses that theme
 * rather than carrying its own identity colour.
 */
const TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
  CRASH_COURSE: "Crash Course",
  COURSE: "Crash Course",
};

function buildTypeConfig(offeringType: string) {
  const key = offeringType === "COURSE" ? "CRASH_COURSE" : offeringType;
  const theme = getOfferingTypeTheme(key);
  return {
    label: TYPE_LABELS[offeringType] ?? theme.typeLabel,
    icon: theme.icon,
    accent: theme.soft,
    accentText: theme.accent,
    accentBorder: theme.border,
    gradient: theme.gradient,
  };
}

const statusConfig: Record<string, { label: string; dot: string; text: string; bg: string }> = {
  PUBLISHED:      { label: "Live",         dot: "bg-emerald-500 animate-pulse", text: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200" },
  UNPUBLISHED:    { label: "Unpublished",  dot: "bg-slate-400",                 text: "text-slate-600",  bg: "bg-slate-100 border border-slate-200" },
  DRAFT:          { label: "Draft",        dot: "bg-amber-400",                 text: "text-amber-700",  bg: "bg-amber-50 border border-amber-200" },
  PENDING_REVIEW: { label: "Under Review", dot: "bg-sky-400",                   text: "text-sky-700",    bg: "bg-sky-50 border border-sky-200" },
};

const DAY_SHORT: Record<string, string> = {
  MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed",
  THURSDAY: "Thu", FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun",
};

/* ─── helpers ─── */
const fmtTime = (t: string) => {
  const [hh, mm] = t.split(":").map(Number);
  const ampm = hh < 12 ? "AM" : "PM";
  const dh = hh % 12 === 0 ? 12 : hh % 12;
  return `${dh}:${String(mm).padStart(2, "0")} ${ampm}`;
};

/* ─── main ─── */
export default function ViewOfferingPage() {
  usePageTitle('Offering Details | BookMyCoaching');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const auth = useAuth();
  const fromTab = (location.state as { fromTab?: string } | null)?.fromTab;
  const backToOfferings = () => navigate("/teacher-offerings", { state: fromTab ? { activeTab: fromTab } : undefined });
  const { teacherProfile, hasActiveVersion } = useTeacherProfile();

  const [offering, setOffering] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const myOfferings = useMyOfferings(
    offering?.offeringType === 'ONE_TO_ONE' || offering?.offeringType === 'GROUP' || offering?.offeringType === 'CRASH_COURSE'
  );
  const [showPublishConfirm, setShowPublishConfirm]   = useState(false);
  const [publishing, setPublishing]                   = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [unpublishing, setUnpublishing]               = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm]     = useState(false);
  const [deleting, setDeleting]                       = useState(false);
  // F15 — 409 from POST /publish (OFFERING_TYPE_NOT_ALLOWED | OFFERING_LIMIT_REACHED),
  // recovered from the caught error's message. See src/lib/publishRestrictions.ts.
  const [publishBlock, setPublishBlock] = useState<OfferingPublishBlockReason | null>(null);

  const teachingModes = useTeachingModes();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();
  const acceptedLevels = useAcceptedLevels();

  // F15 — best-effort, proactive publish-restriction check (TEACHER_TIER_BENEFITS).
  // The 409 from POST /publish (captured into `publishBlock`) remains the source
  // of truth; this only lets the CTA warn/disable itself before an attempt, and
  // lets the Unpublish button warn before the click that a since-narrowed plan
  // won't allow republishing this offering's type.
  const tierBenefitsOn = isReleaseFlagOn('TEACHER_TIER_BENEFITS');
  const entitlements = useTeacherEntitlements();
  const proactivePublishBlock = tierBenefitsOn
    ? computeProactivePublishBlock(offering?.offeringType, entitlements.data)
    : null;
  const effectivePublishBlock = publishBlock ?? proactivePublishBlock;
  const unpublishWouldBlockRepublish = tierBenefitsOn
    && offering?.status === 'PUBLISHED'
    && !isOfferingTypeStillAllowed(offering?.offeringType, entitlements.data);

  const profileApproved = hasActiveVersion;
  const teacherName    = teacherProfile?.fullName || "";
  const teacherAvatar  = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => (typeof s === "string" ? s : s?.subjectName || s?.subject?.name || ""))
    .filter(Boolean);

  useEffect(() => {
    if (!id) return;
    const token = auth.user?.access_token || sessionStorage.getItem("access_token");
    if (!token) { setLoading(false); return; }
    getOfferingById(id, token)
      .then(setOffering)
      .catch(err => {
        console.error(err);
        toast({ title: "Failed to load offering", variant: "destructive" });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handlePublish = async () => {
    const token = await auth.getToken();
    if (!token || !id) return;
    // Same guard as the wizard: never publish with unfilled template [placeholders].
    const leftover = findLeftoverPlaceholders({
      title: offering?.title,
      description: offering?.description,
      learningOutcomes: offering?.learningOutcomes,
      prerequisites: offering?.prerequisites,
    });
    if (leftover.length > 0) {
      setShowPublishConfirm(false);
      toast({
        title: "Cannot publish — [placeholders] left to fill in",
        description: `Replace the [bracketed] template text in: ${leftover.join(', ')}. Open Edit to fix it.`,
        variant: "destructive",
      });
      return;
    }
    setPublishing(true);
    setPublishBlock(null);
    try {
      if (offering?.offeringType === 'ONE_TO_ONE' || offering?.offeringType === 'GROUP' || offering?.offeringType === 'CRASH_COURSE') {
        const result = await myOfferings.refetch();
        if (result.isError || result.status === 'error' || result.data === undefined) {
          toast({ title: "Could not verify your existing offerings", description: "Please try again.", variant: "destructive" });
          return;
        }
        const conflicts = checkOfferingDuplicates({
          title: offering.title,
          subjectId: offering.subjectId,
          gradeBands: offering.gradeBands ?? [],
          curriculumSystems: offering.curriculumSystems ?? [],
          offeringType: offering.offeringType,
          editingOfferingId: id,
          existingOfferings: result.data,
          nextBatchStartDate: offering.offeringType === 'GROUP' ? offering.group?.nextBatchStartDate : undefined,
          courseStartDate: offering.offeringType === 'CRASH_COURSE' ? offering.crashCourse?.courseStartDate : undefined,
          courseEndDate: offering.offeringType === 'CRASH_COURSE' ? offering.crashCourse?.courseEndDate : undefined,
        });
        if (conflicts.hasAny) {
          const messages = [conflicts.title, ...conflicts.gradeBands, ...conflicts.curriculumSystems, ...conflicts.nextBatchDate, ...conflicts.courseDates].filter(Boolean) as string[];
          toast({
            title: "Cannot publish — duplicate offering",
            description: messages.join(' '),
            variant: "destructive",
          });
          return;
        }
      }
      await publishOfferingById(id, token);
      toast({ title: "Offering published!", description: "Your offering is now visible to students." });
      backToOfferings();
    } catch (err: any) {
      // F15 — a 409 here means the teacher's subscription tier blocks this
      // publish (wrong offering type, or the plan's publish limit is reached).
      // Keep the confirm dialog closed but surface the specific restriction
      // inline on the page instead of just a generic toast.
      const blocked = parsePublishBlockReason(err);
      if (blocked) {
        setPublishBlock(blocked);
        toast({
          title: "Publishing is blocked by your current subscription plan",
          description: "See the details on the page below.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Failed to publish", description: err.message || "Something went wrong.", variant: "destructive" });
      }
    } finally { setPublishing(false); setShowPublishConfirm(false); }
  };

  const handleUnpublish = async () => {
    const token = await auth.getToken();
    if (!token || !id) return;
    setUnpublishing(true);
    try {
      await unpublishOfferingById(id, token);
      toast({ title: "Offering unpublished", description: "Your offering is no longer visible to students." });
      backToOfferings();
    } catch (err: any) {
      toast({ title: "Failed to unpublish", description: err.message || "Something went wrong.", variant: "destructive" });
    } finally { setUnpublishing(false); setShowUnpublishConfirm(false); }
  };

  const handleDelete = async () => {
    const token = await auth.getToken();
    if (!token || !id) return;
    setDeleting(true);
    try {
      await deleteTeacherOfferings(token, id);
      void queryClient.invalidateQueries({ queryKey: ['teacherOfferings'] });
      toast({ title: "Offering deleted", description: "The offering has been permanently removed." });
      backToOfferings();
    } catch (err: any) {
      toast({ title: "Failed to delete offering", description: err.message || "Something went wrong.", variant: "destructive" });
    } finally { setDeleting(false); setShowDeleteConfirm(false); }
  };

  const config    = offering ? buildTypeConfig(offering.offeringType ?? offering.type ?? "ONE_TO_ONE") : null;
  const Icon      = config?.icon;
  const statusCfg = offering ? (statusConfig[offering.status] ?? statusConfig.DRAFT) : null;

  const cloneOffering = () => {
    try { sessionStorage.setItem("cloneFromOffering", JSON.stringify(offering)); } catch { /* ignore */ }
    const t = (offering?.offeringType || offering?.type || "").toString().toUpperCase();
    const slug = t === "GROUP" ? "group" : (t === "CRASH_COURSE" || t === "COURSE" ? "crash_course" : "one_to_one");
    navigate(`/create-offering/${slug}`, { state: { cloneFrom: offering, fromClone: true } as any });
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar teacherName={teacherName} teacherAvatar={teacherAvatar} subjects={teacherSubjects} />

          <div className="flex-1 flex flex-col min-w-0">

            <main className="flex-1 overflow-auto bg-slate-50">

              {loading && (
                <div className="flex items-center justify-center py-40">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
                </div>
              )}
              {!loading && !offering && (
                <div className="text-center py-40 text-slate-400 text-sm">Offering not found.</div>
              )}

              {!loading && offering && config && (
                <div className="max-w-7xl">

                  {/* ── BACK LINK ── */}
                  <div className="px-5 pt-5 pb-1">
                    <BackLink label="My Offerings" onClick={backToOfferings} />
                  </div>

                  {/* ── HERO BANNER ── */}
                  <div className={cn("bg-gradient-to-br", config.gradient, "text-white px-6 py-8 mx-5 mt-2 rounded-2xl mb-6")}>
                    {/* Type chip + status badge */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-lg">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {config.label}
                      </span>
                      {statusCfg && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-lg bg-white/15 text-white">
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                          {statusCfg.label}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl font-bold text-white leading-tight mb-1">
                      {offering.title}
                    </h1>
                    {offering.category && (
                      <p className="text-sm text-white/60 mb-4">
                        {offering.category.replace(/_/g, " ")}
                      </p>
                    )}

                    {/* Stats row as white/translucent pills */}
                    {(offering.subjectName || offering.teachingMode || offering.oneToOne?.sessionDurationMinutes || offering.group?.sessionDurationMinutes || offering.group?.sessionsPerWeek || offering.crashCourse?.totalSessions || offering.gradeBands?.length > 0) && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {offering.subjectName && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <BookOpen className="w-3.5 h-3.5 opacity-80" />
                            {offering.subjectName}
                          </span>
                        )}
                        {offering.teachingMode && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <MapPin className="w-3.5 h-3.5 opacity-80" />
                            {teachingModes.labelOf(offering.teachingMode)}
                          </span>
                        )}
                        {offering.oneToOne?.sessionDurationMinutes && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <Clock className="w-3.5 h-3.5 opacity-80" />
                            {offering.oneToOne.sessionDurationMinutes} min
                          </span>
                        )}
                        {offering.group?.sessionDurationMinutes && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <Clock className="w-3.5 h-3.5 opacity-80" />
                            {offering.group.sessionDurationMinutes} min
                          </span>
                        )}
                        {offering.group?.sessionsPerWeek && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <BarChart2 className="w-3.5 h-3.5 opacity-80" />
                            {offering.group.sessionsPerWeek}× / week
                          </span>
                        )}
                        {offering.crashCourse?.totalSessions && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <BarChart2 className="w-3.5 h-3.5 opacity-80" />
                            {offering.crashCourse.totalSessions} sessions
                          </span>
                        )}
                        {offering.gradeBands?.length > 0 && (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/15 px-3 py-1.5 rounded-full text-white">
                            <GraduationCap className="w-3.5 h-3.5 opacity-80" />
                            {offering.gradeBands.map((g: string) => gradeBands.labelOf(g)).join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Two-column layout ── */}
                  <div className="flex flex-col lg:flex-row gap-8 items-start px-5 pb-8">

  {/* ── LEFT — borderless sections ── */}
  <div className="flex-1 min-w-0 space-y-0 divide-y divide-slate-100">

    {/* About */}
    {(offering.description || offering.topicNames?.length > 0) && (
      <div className="py-6 first:pt-0">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">About this Offering</h3>
        </div>
        {offering.description && (
          <div
            className="text-[15px] text-slate-600 leading-relaxed prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: offering.description }}
          />
        )}
        {offering.topicNames?.length > 0 && (
          <div className={offering.description ? "mt-4" : ""}>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Topics Covered</p>
            <div className="flex flex-wrap gap-2">
              {offering.topicNames.map((t: string) => (
                <span key={t} className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full ${config.accent} ${config.accentText} border ${config.accentBorder}`}>
                  <Tag className="w-3 h-3" />{t}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}

    {/* What You'll Learn */}
    {(offering.learningOutcomes || offering.prerequisites || offering.targetScorePromise) && (
      <div className="py-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">What You'll Learn</h3>
        </div>
        <div className="space-y-4">
          {offering.learningOutcomes && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-1.5 flex items-center gap-1.5">
                <Target className="w-3 h-3" /> Outcomes
              </p>
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.learningOutcomes }} />
            </div>
          )}
          {offering.prerequisites && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-violet-600 mb-1.5 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Prerequisites
              </p>
              <div className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.prerequisites }} />
            </div>
          )}
          {offering.targetScorePromise && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-1.5 flex items-center gap-1.5">
                <Star className="w-3 h-3" /> Score Promise
              </p>
              <p className="text-sm text-slate-700">{offering.targetScorePromise}</p>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Session Details — 1-on-1 */}
    {offering?.oneToOne && (
      <div className="py-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">Session Details</h3>
        </div>
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Duration</span>
          <span className="text-sm font-semibold text-slate-900">{offering.oneToOne.sessionDurationMinutes} minutes</span>
        </div>
      </div>
    )}

    {/* Session Details — Group */}
    {offering?.group && (
      <div className="py-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">Group Batch Details</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {offering.group.batchSizeMin != null && offering.group.batchSizeMax != null && (
            <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${config.accent} ${config.accentText} border ${config.accentBorder}`}>
              <Users className="w-3.5 h-3.5 shrink-0" />
              Batch: <span className="font-semibold">{offering.group.batchSizeMin}–{offering.group.batchSizeMax}</span>
            </span>
          )}
          {offering.group.sessionsPerWeek != null && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <BarChart2 className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.group.sessionsPerWeek}</span> sessions/week
            </span>
          )}
          {offering.group.sessionDurationMinutes != null && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.group.sessionDurationMinutes}</span> min
            </span>
          )}
          {offering.group.totalSessionsPlanned != null && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <BookOpen className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.group.totalSessionsPlanned}</span> total sessions
            </span>
          )}
          {offering.group.nextBatchStartDate && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CalendarDays className="w-3.5 h-3.5 shrink-0" />
              Next batch: <span className="font-semibold">{offering.group.nextBatchStartDate}</span>
            </span>
          )}
          {offering.group.rollingEnrollment && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 font-semibold">
              Rolling Enrollment{offering.group.rollingEnrollmentCutoffWeek ? ` · until week ${offering.group.rollingEnrollmentCutoffWeek}` : ""}
            </span>
          )}
          {offering.computedFields?.currentEnrollment != null && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <Users className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.computedFields.currentEnrollment}</span> enrolled
            </span>
          )}
        </div>

        {(offering.group.curriculumMilestones ?? offering.group.milestones)?.length > 0 && (
          <>
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">Curriculum Milestones</p>
            <div className="space-y-0 divide-y divide-slate-100">
              {(offering.group.curriculumMilestones ?? offering.group.milestones).map((m: any, i: number) => (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <span className={`text-xs font-extrabold px-2 py-1 rounded-lg ${config.accent} ${config.accentText} shrink-0`}>W{m.weekNumber}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  <span className="text-sm text-slate-700 flex-1">{m.title}</span>
                  {m.hasAssessment && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold border border-amber-200 shrink-0">Assessment</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )}

    {/* Session Details — Crash Course */}
    {offering?.crashCourse && (
      <div className="py-6 space-y-5">
        <div className="flex items-center gap-2.5">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">Crash Course Details</h3>
        </div>

        {(offering.crashCourse.courseStartDate || offering.crashCourse.courseEndDate) && (
          <div className="flex items-center gap-3">
            <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="text-sm text-slate-500">Course Dates</span>
            <span className="text-sm font-semibold text-slate-900">
              {offering.crashCourse.courseStartDate} → {offering.crashCourse.courseEndDate}
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {offering.crashCourse.totalSessions && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <Layers className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.crashCourse.totalSessions}</span> sessions
            </span>
          )}
          {offering.crashCourse.sessionsPerWeek && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.crashCourse.sessionsPerWeek}×</span> per week
            </span>
          )}
          {offering.crashCourse.seatsTotal && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <Users className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold">{offering.crashCourse.seatsTotal}</span>
              {offering.computedFields?.seatsRemaining != null && (
                <span className="text-slate-500">({offering.computedFields.seatsRemaining} left)</span>
              )}
            </span>
          )}
          {offering.crashCourse.minimumEntryLevel && (
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">
              <GraduationCap className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold capitalize">{offering.crashCourse.minimumEntryLevel.replace(/_/g, " ").toLowerCase()}</span>
            </span>
          )}
        </div>

        {offering.crashCourse.weekPlan?.length > 0 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Week-by-Week Plan · {offering.crashCourse.weekPlan.length} weeks
            </p>
            <div className="relative">
              <div className="absolute left-[22px] top-6 bottom-4 w-px bg-gradient-to-b from-slate-200 to-transparent" />
              <div className="space-y-3">
                {offering.crashCourse.weekPlan.map((w: any, i: number) => (
                  <div key={i} className="flex gap-3">
                    <div className="shrink-0 w-11 h-11 rounded-full bg-slate-700 text-white flex flex-col items-center justify-center shadow-sm z-10 mt-0.5">
                      <span className="text-xs font-bold uppercase leading-none opacity-60">Wk</span>
                      <span className="text-sm font-extrabold leading-none">{w.weekNumber}</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2 py-4 border-b border-slate-100 last:border-0">
                      <span className="text-sm font-semibold text-slate-800 flex-1">
                        {w.theme || <span className="text-slate-300 italic">No theme set</span>}
                      </span>
                      <div className="flex gap-1.5 shrink-0">
                        {w.mockTest && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                            <FlaskConical className="w-3 h-3" /> Mock Test
                          </span>
                        )}
                        {w.revisionSession && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                            <RefreshCw className="w-3 h-3" /> Revision
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Curriculum & Levels */}
    {(offering.curriculumSystems?.length > 0 || offering.acceptedLevels?.length > 0) && (
      <div className="py-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-1 h-5 rounded-full bg-primary shrink-0" />
          <h3 className="text-base font-semibold text-slate-900">Curriculum &amp; Levels</h3>
        </div>
        <div className="space-y-3">
          {offering.curriculumSystems?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Curriculum Systems</p>
              <div className="flex flex-wrap gap-2">
                {offering.curriculumSystems.map((c: string) => (
                  <span key={c} className="text-sm font-medium px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {curricula.labelOf(c)}
                  </span>
                ))}
              </div>
            </div>
          )}
          {offering.acceptedLevels?.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Accepted Levels</p>
              <div className="flex flex-wrap gap-2">
                {offering.acceptedLevels.map((l: string) => (
                  <span key={l} className="text-sm font-medium px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {acceptedLevels.labelOf(l)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )}

                  </div>{/* end left col */}

                  {/* ── RIGHT — single consolidated card ── */}
                  <div className="w-full lg:w-[300px] shrink-0 lg:sticky lg:top-20">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

      {/* Pricing */}
      {offering.pricingPlans?.length > 0 && (
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Price</p>
          <div className="space-y-2">
            {offering.pricingPlans.map((p: any) => {
              const sym = currencySymbol(p.currency);
              return (
                <div key={p.currency} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {/* SCRUM-365: symbol only, no currency-code label (W6). */}
                  <span className="text-sm font-semibold text-slate-900">{sym}{p.regularPrice}</span>
                  {p.earlyBirdPrice != null && p.earlyBirdPrice !== "" && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      Early bird {sym}{p.earlyBirdPrice}
                    </span>
                  )}
                  {p.trialSessionPrice != null && p.trialSessionPrice !== "" && (
                    <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      Trial {sym}{p.trialSessionPrice}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trial / Early bird flags */}
      {(offering.trialSessionAvailable || offering.earlyBirdDeadline) && (
        <div className="px-5 py-3 border-b border-slate-100 space-y-2">
          {offering.trialSessionAvailable && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <Star className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium">Trial session available</span>
            </div>
          )}
          {offering.earlyBirdDeadline && (
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <CalendarDays className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium">Early bird until {offering.earlyBirdDeadline}</span>
            </div>
          )}
        </div>
      )}

      {/* Availability */}
      {offering.availabilitySlots?.length > 0 && (
        <div className="border-b border-slate-100">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 px-5 pt-4 pb-2">Availability</p>
          <div className="divide-y divide-slate-100">
            {offering.availabilitySlots.map((s: any, i: number) => (
              <div key={i} className="flex items-center justify-between px-5 py-2.5">
                <span className={`text-sm font-semibold ${config.accentText}`}>
                  {DAY_SHORT[s.dayOfWeek] || s.dayOfWeek?.slice(0, 3)}
                </span>
                <span className="text-sm font-medium text-slate-900">
                  {s.timeFrom ? fmtTime(s.timeFrom.slice(0, 5)) : ""}{" "}
                  {s.timeTo ? `– ${fmtTime(s.timeTo.slice(0, 5))}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile warning */}
      {!profileApproved && (
        <div className="px-5 py-3 border-b border-slate-100 flex items-start gap-2.5">
          <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Profile not approved yet</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">You can publish now, but your offering won't show in student search until your profile is approved.</p>
          </div>
        </div>
      )}

      {/* F15 — warn BEFORE the Unpublish click, not just inside its confirm dialog:
          once unpublished, republishing is subject to today's allowed-types list,
          so a teacher who unpublishes a now-disallowed type to edit it could get
          stuck unable to put it back live. */}
      {unpublishWouldBlockRepublish && (
        <div className="px-5 py-3 border-b border-slate-100 flex items-start gap-2.5 bg-amber-50">
          <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Unpublishing will lock this offering</p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              Your current plan no longer allows publishing {TYPE_LABELS[offering.offeringType] ?? "this offering type"} offerings.
              If you unpublish, you won't be able to republish it until you upgrade your plan.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 py-4 space-y-2">
        {(offering.status === "DRAFT" || offering.status === "UNPUBLISHED") && (
          <Button className="w-full gap-2" onClick={() => navigate(`/edit-offering/${id}`)}>
            <Pencil className="w-4 h-4" /> Edit Offering
          </Button>
        )}
        {(offering.status === "DRAFT" || offering.status === "UNPUBLISHED") && tierBenefitsOn && effectivePublishBlock && (
          <PublishRestrictionNotice reason={effectivePublishBlock} offeringType={offering.offeringType} />
        )}
        {(offering.status === "DRAFT" || offering.status === "UNPUBLISHED") && (
          <Button
            className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowPublishConfirm(true)}
            disabled={Boolean(tierBenefitsOn && effectivePublishBlock)}
            title={tierBenefitsOn && effectivePublishBlock ? "Blocked by your current subscription plan — see the notice above" : undefined}
          >
            <CheckCircle className="w-4 h-4" />
            {offering.status === "UNPUBLISHED" ? "Re-publish" : "Publish"}
          </Button>
        )}
        {offering.status === "UNPUBLISHED" && tierBenefitsOn && !effectivePublishBlock && (
          <p className="flex items-start gap-1.5 text-xs text-slate-400 leading-relaxed px-0.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            Republishing counts as newly published — if your plan's offering limit is ever enforced again, this offering is treated as your newest.
          </p>
        )}
        {offering.status === "PUBLISHED" && (
          <Button variant="outline" className="w-full gap-2" onClick={() => setShowUnpublishConfirm(true)}>
            <CheckCircle className="w-4 h-4" /> Unpublish
          </Button>
        )}
        {offering.status === "PUBLISHED" && offering.slug && teacherProfile?.handle && (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              const url = `${window.location.origin}/t/${teacherProfile.handle}/${offering.slug}`;
              navigator.clipboard.writeText(url).then(() =>
                toast({ title: "Share link copied!", description: url })
              );
            }}
          >
            <Copy className="w-4 h-4" /> Copy Share Link
          </Button>
        )}
        <Button variant="outline" className="w-full gap-2" onClick={cloneOffering}>
          <Copy className="w-4 h-4" /> Clone Offering
        </Button>
        {isReleaseFlagOn("CLASS_MANAGEMENT") && id && (
          <Button variant="outline" className="w-full gap-2 cursor-pointer transition-colors duration-150" asChild>
            <Link to={`/teacher-classes/${id}`}>
              <GraduationCap className="w-4 h-4" /> Manage Class
            </Link>
          </Button>
        )}
        <Button
          variant="outline"
          className="w-full gap-2 text-destructive border-destructive/30 hover:bg-destructive/5"
          disabled={offering.status === "PUBLISHED"}
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Trash2 className="w-4 h-4" /> Delete
        </Button>
      </div>

                    </div>{/* end single card */}
                  </div>{/* end right col */}
                </div>{/* end two-col */}
              </div>
              )}
            </main>
          </div>
        </div>
      </SidebarProvider>

      {/* ── Dialogs ── */}
      <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Offering</AlertDialogTitle>
            <AlertDialogDescription>
              This will make your offering visible to all students. Are you sure you want to publish?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish} disabled={publishing}>
              {publishing ? "Publishing…" : "Yes, Publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showUnpublishConfirm} onOpenChange={setShowUnpublishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish Offering</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This will hide your offering from students.{unpublishWouldBlockRepublish ? "" : " You can re-publish it later."}</p>
                {unpublishWouldBlockRepublish && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4" /> You won't be able to republish it
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Your current plan no longer allows {TYPE_LABELS[offering?.offeringType] ?? "this"} offerings. Upgrade your plan first if you want to bring it back later.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnpublish} disabled={unpublishing}>
              {unpublishing ? "Unpublishing…" : "Yes, Unpublish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this offering?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {offering?.status === "PUBLISHED" ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4" /> Cannot delete a published offering
                    </p>
                    <p className="text-xs text-amber-700 mt-1">Unpublish it first.</p>
                  </div>
                ) : (
                  <>
                    <p>This action is <strong>permanent and cannot be undone</strong>. Deleting this offering will:</p>
                    <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                      <li>Remove it from your offerings list immediately</li>
                      <li>Make it unavailable to any students who bookmarked it</li>
                      <li>Delete all associated configuration and pricing data</li>
                    </ul>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Offering</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting || offering?.status === "PUBLISHED"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Yes, Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
